import * as React from "react";
import { Card, CardContent } from "../../components/ui/card.tsx";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { ChartPie } from "lucide-react";
import { useGetStudentPerformance } from "../../api/performance/queries";

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-[300px] text-center p-6">
    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
      <ChartPie className="w-8 h-8 text-purple-600" />
    </div>
    <h3 className="text-lg font-medium mb-2">No Performance Data</h3>
    <p className="text-sm text-gray-500 max-w-[250px]">
      Start recording student scores to see daily insights
    </p>
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center h-[300px]">
    <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
  </div>
);

const formatPerformanceData = (rawData) => {
  if (!rawData) return [];

  return rawData.map(data => ({
    class: data.className,
    value: data.averageScore,
    count: data.studentCount
  }));
};

const getBarColor = (score) => {
  if (score >= 90) return "#22C55E"; // green-500
  if (score >= 70) return "#F97316"; // orange-500  
  return "#EF4444"; // red-500
};

export default function PerformanceChart() {
  const [isMobile, setIsMobile] = React.useState(false);
  const { data: performanceData, isLoading } = useGetStudentPerformance();

  // Check if we're on mobile device
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const data = React.useMemo(() => formatPerformanceData(performanceData), [performanceData]);
  const isEmpty = !isLoading && (!data || data.length === 0);

  return (
    <Card className="w-full h-[360px] sm:h-[360px]">
      <CardContent className="p-3 sm:p-6 flex flex-col h-full">
        <h3 className="text-base font-semibold mb-2 sm:mb-4">Student Performance Today</h3>

        {isLoading ? <LoadingState /> : isEmpty ? <EmptyState /> : (
          <div className="flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{
                  top: 5,
                  right: isMobile ? 5 : 15,
                  left: isMobile ? -25 : -15,
                  bottom: 20,
                }}
                barGap={isMobile ? 6 : 12}
              >
                <XAxis
                  dataKey="class"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6B7280", fontSize: isMobile ? 10 : 12 }}
                  dy={10}
                  interval={isMobile ? 1 : 0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#6B7280", fontSize: isMobile ? 10 : 12 }}
                  dx={-10}
                  domain={[0, 100]}
                  ticks={[0, 30, 65, 100]}
                />
                <Bar
                  dataKey="value"
                  radius={[isMobile ? 15 : 25, isMobile ? 15 : 25, isMobile ? 15 : 25, isMobile ? 15 : 25]}
                  barSize={isMobile ? 20 : 30}
                >
                  {data.map((entry, index) => (
                    <cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
